import { h } from "preact";
import { fontFamilyStyle } from "./monogram-fonts.js";
import {
  GOLD_FOIL,
  HAND_PAINTED,
  fontScale,
  maxLettersFor,
  phrasesFor,
  sanitizeText,
  sizesFor,
} from "./monogram-options.js";

const t = (strings, key, variables) =>
  window.theme.translations(strings, key, variables);

function RadioRow({ name, value, checked, disabled, onChange, children }) {
  return (
    <label class="monogram-option radio-label">
      <input
        type="radio"
        class="radio-input"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span class="radio-circle" aria-hidden="true" />
      {children}
    </label>
  );
}

// A radio drawn as a checkbox, per the design: the choice is exclusive, so
// it keeps radio semantics and arrow-key behaviour.
function EntryOption({ value, checked, onChange, children }) {
  return (
    <label class="monogram-option checkbox-label body-small">
      <span class="checkbox-wrapper">
        <input
          type="radio"
          name="monogram-entry"
          value={value}
          checked={checked}
          onChange={onChange}
        />
        <span class="checkbox-icon" aria-hidden="true" />
      </span>
      {children}
    </label>
  );
}

export function TypeSelector({ types, selection, strings, onChange }) {
  const label = (type) =>
    t(strings, type === GOLD_FOIL ? "type_gold_foil" : "type_hand_painted");

  if (types.length === 1) {
    return <p class="monogram__type utility">{label(types[0])}</p>;
  }

  return (
    <fieldset class="monogram-types">
      <legend class="visually-hidden">{t(strings, "type_label")}</legend>
      {types.map((type) => (
        <label class="monogram-types__option">
          <input
            type="radio"
            class="monogram-types__input"
            name="monogram-type"
            value={type}
            checked={selection.type === type}
            onChange={() => onChange({ type })}
          />
          <span class="monogram-types__label utility">{label(type)}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function TextStep({
  config,
  selection,
  rules,
  font,
  prices,
  strings,
  onChange,
}) {
  const phrases = phrasesFor(config, selection);
  const placeholder = t(
    strings,
    rules.numbers ? "placeholder_characters" : "placeholder_letters",
    {
      count: rules.max,
    },
  );

  const onInput = (event) => {
    const value = sanitizeText(event.currentTarget.value, rules);
    // Written back to the field directly: a rejected character leaves state
    // unchanged, so no re-render would remove it.
    event.currentTarget.value = value;
    onChange({ text: value });
  };

  return (
    <div class="monogram-text">
      {phrases.length > 0 && (
        <div
          class="monogram-text__entry"
          role="radiogroup"
          aria-label={t(strings, "step_text")}
        >
          <EntryOption
            value="text"
            checked={selection.entry === "text"}
            onChange={() => onChange({ entry: "text" })}
          >
            {t(strings, "entry_characters")}
          </EntryOption>
          <EntryOption
            value="phrase"
            checked={selection.entry === "phrase"}
            onChange={() => onChange({ entry: "phrase" })}
          >
            {t(strings, "entry_phrases")}
          </EntryOption>
        </div>
      )}

      {selection.entry === "phrase" ? (
        <div
          class="monogram-options monogram-text__phrases"
          role="radiogroup"
          aria-label={t(strings, "select_phrase")}
        >
          {phrases.map((phrase) => (
            <RadioRow
              name="monogram-phrase"
              value={phrase}
              checked={selection.phrase === phrase}
              onChange={() => onChange({ phrase })}
            >
              <span class="body">{phrase}</span>
            </RadioRow>
          ))}
          {prices.goldFoil && (
            <p class="field__hint monogram-text__hint">
              {t(strings, "price_phrases", { price: prices.goldFoil })}
            </p>
          )}
        </div>
      ) : (
        <div class="field">
          <input
            type="text"
            class="monogram-text__input"
            aria-label={t(strings, "text_label")}
            placeholder={placeholder}
            maxLength={rules.max}
            value={selection.text}
            autocomplete="off"
            autocapitalize="characters"
            spellcheck={false}
            onInput={onInput}
          />
          {!rules.numbers && font?.numbersNotAvailableMessage && (
            <p class="field__hint monogram-text__hint">
              {font.numbersNotAvailableMessage}
            </p>
          )}
          {selection.type === GOLD_FOIL
            ? prices.goldFoil && (
                <p class="field__hint monogram-text__hint">
                  {t(strings, "price_gold_foil", {
                    price: prices.goldFoil,
                    count: rules.max,
                  })}
                </p>
              )
            : [
                prices.firstLetter &&
                  t(strings, "price_first_letter", {
                    price: prices.firstLetter,
                  }),
                prices.additionalLetter &&
                  t(strings, "price_additional_letter", {
                    price: prices.additionalLetter,
                  }),
              ]
                .filter(Boolean)
                .map((hint) => (
                  <p class="field__hint monogram-text__hint">{hint}</p>
                ))}
        </div>
      )}
    </div>
  );
}

export function FontStep({ fonts, selection, onChange }) {
  return (
    <div class="monogram-options">
      {fonts.map((font) => {
        const style = {
          fontFamily: fontFamilyStyle(font.url),
          "--monogram-font-scale": fontScale(font),
        };

        return (
          <RadioRow
            name="monogram-font"
            value={font.id}
            checked={selection.fontId === font.id}
            onChange={() => onChange({ fontId: font.id })}
          >
            <span class="monogram-font__name" style={style}>
              {font.name}
            </span>
            {font.previewLetters && (
              <span
                class="monogram-font__preview"
                style={style}
                aria-hidden="true"
              >
                {font.previewLetters}
              </span>
            )}
          </RadioRow>
        );
      })}
    </div>
  );
}

export function SizeStep({ placement, selection, strings, onChange }) {
  const sizeLabel = (size) =>
    t(strings, size === 2 ? "size_two_inch" : "size_one_inch");

  return (
    <div class="monogram-options">
      {sizesFor(placement).map((size) => (
        <div class="monogram-size">
          <RadioRow
            name="monogram-size"
            value={size}
            checked={selection.size === size}
            onChange={() => onChange({ size })}
          >
            <span class="body">{sizeLabel(size)}</span>
          </RadioRow>
          {size === 2 && (
            <p class="field__hint monogram-size__hint">
              {t(strings, "size_max_letters", {
                count: maxLettersFor(placement, HAND_PAINTED, size),
                size: sizeLabel(size),
              })}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function Swatches({ name, legend, options, selectedId, disabledId, onChange }) {
  const selected = options.find((option) => option.id === selectedId);

  return (
    <fieldset class="monogram-swatches">
      <legend class="monogram-swatches__legend body">
        <strong>{legend}</strong> {selected?.label}
      </legend>
      <div class="monogram-swatches__list">
        {options.map((option) => (
          <label class="monogram-swatch" title={option.label}>
            <input
              type="radio"
              class="monogram-swatch__input"
              name={name}
              value={option.id ?? ""}
              checked={option.id === selectedId}
              disabled={option.id !== null && option.id === disabledId}
              onChange={() => onChange(option.id)}
            />
            <span
              class={`monogram-swatch__dot${option.id === null ? " monogram-swatch__dot--none" : ""}`}
              style={{ "--swatch-color": option.color }}
              aria-hidden="true"
            />
            <span class="visually-hidden">{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function ColorStep({ config, selection, strings, onChange }) {
  const colors = config.handPainted?.colors ?? [];

  return (
    <div class="monogram-colors">
      <Swatches
        name="monogram-first-color"
        legend={t(strings, "first_color")}
        options={colors}
        selectedId={selection.firstColorId}
        onChange={(firstColorId) => onChange({ firstColorId })}
      />
      <Swatches
        name="monogram-second-color"
        legend={t(strings, "second_color")}
        options={[
          ...colors,
          { id: null, label: t(strings, "color_none"), color: null },
        ]}
        selectedId={selection.secondColorId}
        disabledId={selection.firstColorId}
        onChange={(secondColorId) => onChange({ secondColorId })}
      />
    </div>
  );
}

export function PlacementStep({ placements, selection, onChange }) {
  return (
    <div class="monogram-options">
      {placements.map((placement) => (
        <RadioRow
          name="monogram-placement"
          value={placement.id}
          checked={selection.placementId === placement.id}
          onChange={() => onChange({ placementId: placement.id })}
        >
          <span class="body">{placement.title}</span>
        </RadioRow>
      ))}
    </div>
  );
}
